import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS, getRoleColor } from "./adminStyles";

const API_URL = getBaseURL();

const isValidUrl = (url) => {
  if (!url) return true;
  try { new URL(url); return true; }
  catch { return false; }
};

const ManageTeamsInner = () => {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ name: "", short_code: "", logo_url: "" });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [logoPreviewLoaded, setLogoPreviewLoaded] = useState(false);

  const [expandedTeam, setExpandedTeam] = useState(null);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [playerForm, setPlayerForm] = useState({ name: "", role: "BAT", credit: 9.0, image: "" });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [playerImgError, setPlayerImgError] = useState(false);
  const [playerImgLoaded, setPlayerImgLoaded] = useState(false);

  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchTeams();
  }, []);

  useEffect(() => {
    setLogoPreviewError(false);
    setLogoPreviewLoaded(false);
  }, [form.logo_url]);

  useEffect(() => {
    setPlayerImgError(false);
    setPlayerImgLoaded(false);
  }, [playerForm.image]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/teams`, { headers: { Authorization: `Bearer ${token}` } });
      setTeams(res.data);
    } catch (err) {
      toast.error("Failed to load teams.");
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warn("Team name is required."); return; }
    if (!form.short_code.trim()) { toast.warn("Short code is required."); return; }
    if (form.logo_url && !isValidUrl(form.logo_url)) { toast.warn("Please enter a valid logo URL."); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/admin/teams/${editing}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Team "${form.name}" updated!`);
      } else {
        await axios.post(`${API_URL}/admin/teams`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Team "${form.name}" registered!`);
      }
      setForm({ name: "", short_code: "", logo_url: "" });
      setEditing(null);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save team.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (shortCode, name) => {
    if (!window.confirm(`Delete team "${name}" (${shortCode}) and ALL their players? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/teams/${shortCode}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Team "${name}" and all players deleted.`);
      if (expandedTeam === shortCode) setExpandedTeam(null);
      fetchTeams();
    } catch (err) {
      toast.error("Failed to delete team.");
    }
  };

  const toggleExpand = async (shortCode) => {
    if (expandedTeam === shortCode) {
      setExpandedTeam(null);
      setTeamPlayers([]);
      return;
    }
    setExpandedTeam(shortCode);
    setEditingPlayer(null);
    setPlayerForm({ name: "", role: "BAT", credit: 9.0, image: "" });
    fetchPlayersForTeam(shortCode);
  };

  const fetchPlayersForTeam = async (shortCode) => {
    setPlayerLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/teams/${shortCode}/players`, { headers: { Authorization: `Bearer ${token}` } });
      setTeamPlayers(res.data);
    } catch (err) {
      setTeamPlayers([]);
      toast.error("Failed to load squad.");
    } finally { setPlayerLoading(false); }
  };

  const handlePlayerSubmit = async (e) => {
    e.preventDefault();
    if (!playerForm.name.trim()) { toast.warn("Player name is required."); return; }
    if (playerForm.image && !isValidUrl(playerForm.image)) { toast.warn("Please enter a valid image URL."); return; }
    try {
      if (editingPlayer) {
        await axios.put(`${API_URL}/admin/teams/players/${editingPlayer}`, playerForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`${playerForm.name} updated!`);
      } else {
        await axios.post(`${API_URL}/admin/teams/${expandedTeam}/players`, playerForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`${playerForm.name} added to squad!`);
      }
      setPlayerForm({ name: "", role: "BAT", credit: 9.0, image: "" });
      setEditingPlayer(null);
      fetchPlayersForTeam(expandedTeam);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save player.");
    }
  };

  const handleDeletePlayer = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from squad?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/teams/players/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${name} removed from squad.`);
      fetchPlayersForTeam(expandedTeam);
      fetchTeams();
    } catch (err) {
      toast.error("Failed to remove player.");
    }
  };

  const logoPreviewUrl = form.logo_url && isValidUrl(form.logo_url) ? form.logo_url : null;
  const playerImgPreviewUrl = playerForm.image && isValidUrl(playerForm.image) ? playerForm.image : null;

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>🏏 Teams Database</div>
          <div style={S.pageSubtitle}>Manage Teams & Player Squads</div>
        </div>
        <div style={{ ...S.badge(COLORS.indigo), padding: "10px 16px", fontSize: "13px" }}>
          {teams.length} Teams · {teams.reduce((s, t) => s + Number(t.player_count || 0), 0)} Players
        </div>
      </div>

      {/* ── CONDITIONALLY RENDER TEAM LIST OR DEDICATED SQUAD VIEW ── */}
      {!expandedTeam ? (
        <>
          {/* REGISTER TEAM FORM */}
          <div style={S.card}>
            <div style={S.cardTitle}>{editing ? "✏️ Edit Team" : "🏏 Register New Team"}</div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={S.label}>Team Full Name *</label>
                  <input className="admin-input-focus" style={S.input} placeholder="e.g. Chennai Super Kings" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div>
                  <label style={S.label}>Short Code *</label>
                  <input className="admin-input-focus" style={S.input} placeholder="e.g. CSK" value={form.short_code} onChange={e => setForm({ ...form, short_code: e.target.value.toUpperCase() })} required maxLength={10} />
                </div>
                <div>
                  <label style={S.label}>🔗 Team Logo URL (optional)</label>
                  <input className="admin-input-focus" style={S.input} placeholder="https://example.com/team-logo.png" value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} />
                  {/* Logo Preview */}
                  {logoPreviewUrl && (
                    <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                      {!logoPreviewLoaded && !logoPreviewError && (
                        <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: COLORS.inputBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontSize: "10px", color: COLORS.textMuted }}>...</span>
                        </div>
                      )}
                      {!logoPreviewError && (
                        <img
                          src={logoPreviewUrl}
                          alt="logo preview"
                          onLoad={() => setLogoPreviewLoaded(true)}
                          onError={() => { setLogoPreviewError(true); setLogoPreviewLoaded(false); }}
                          style={{ width: "40px", height: "40px", borderRadius: "10px", objectFit: "contain", background: COLORS.inputBg, padding: "4px", border: `2px solid ${COLORS.green}`, display: logoPreviewLoaded ? "block" : "none" }}
                        />
                      )}
                      <span style={{ fontSize: "11px", fontWeight: 600, color: logoPreviewError ? COLORS.red : (logoPreviewLoaded ? COLORS.green : COLORS.textMuted) }}>
                        {logoPreviewError ? "✗ Invalid image URL" : (logoPreviewLoaded ? "✓ Logo loaded" : "Loading...")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div style={S.btnRow}>
                {editing && (
                  <button type="button" style={S.btnCancel} onClick={() => { setEditing(null); setForm({ name: "", short_code: "", logo_url: "" }); }}>
                    Cancel
                  </button>
                )}
                <button type="submit" disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
                  {saving ? "Saving..." : (editing ? "Update Team" : "Register Team")}
                </button>
              </div>
            </form>
          </div>

          {/* TEAMS GRID */}
          <div style={{ ...S.sectionTitle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
             <span>Registered Teams ({teams.length})</span>
             <span style={{ fontSize: "11px", color: COLORS.textMuted, fontWeight: 500 }}>Click a team card to manage its squad</span>
          </div>

          {loading ? (
            <div style={S.loading}>
               <div style={{ width: 40, height: 40, border: "3px solid #1e293b", borderTop: "3px solid #3b82f6", borderRadius: "50%", margin: "0 auto 10px", animation: "spin 1s linear infinite" }}></div>
               Loading teams...
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "20px", marginBottom: "40px" }}>
              {teams.map(team => (
                  <div
                    key={team.id || team.short_code}
                    onClick={() => toggleExpand(team.short_code)}
                    className="admin-card-hover"
                    style={{
                      background: COLORS.card,
                      border: `2px solid ${COLORS.cardBorder}`,
                      borderRadius: "20px",
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    {/* Team Logo Area */}
                    <div style={{ height: 140, background: `linear-gradient(135deg, ${COLORS.indigo}05 0%, ${COLORS.indigo}15 100%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                       {team.logo_url && isValidUrl(team.logo_url) ? (
                         <img
                           src={team.logo_url}
                           alt={team.name}
                           style={{ width: "70%", height: "70%", objectFit: "contain", filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.2))" }}
                         />
                       ) : (
                         <div style={{ fontSize: "40px", fontWeight: 900, color: COLORS.indigo, opacity: 0.4 }}>{team.short_code}</div>
                       )}
                       <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
                          <button style={{ ...S.miniBtn, background: "rgba(0,0,0,0.4)" }} onClick={() => { setEditing(team.id || team.short_code); setForm({ name: team.name, short_code: team.short_code, logo_url: team.logo_url || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }}>✏️</button>
                          <button style={{ ...S.miniBtn, background: "rgba(220,38,38,0.1)", color: COLORS.red }} onClick={() => handleDelete(team.short_code, team.name)}>🗑️</button>
                       </div>
                    </div>
                    {/* Info */}
                    <div style={{ padding: "16px", textAlign: "center", borderTop: `1px solid ${COLORS.cardBorder}` }}>
                       <div style={{ fontSize: "14px", fontWeight: 800, color: COLORS.text, textTransform: "uppercase" }}>{team.name}</div>
                       <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 6 }}>
                          <span style={{ ...S.badge(COLORS.indigo), padding: "2px 8px", fontSize: "10px" }}>{team.short_code}</span>
                          <span style={{ fontSize: "11px", color: COLORS.textMuted, fontWeight: 700 }}>{team.player_count || 0} PLAYERS</span>
                       </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      ) : (
        /* ── DEDICATED SQUAD VIEW (The "Inside" Page) ── */
        <div style={{ 
          background: COLORS.card, 
          borderRadius: "24px", 
          border: `1px solid ${COLORS.indigo}30`, 
          padding: "30px",
          minHeight: "80vh",
          animation: "fadeIn 0.4s ease-out"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
             <h2 style={{ fontSize: 24, fontWeight: 800, color: "white", display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={() => setExpandedTeam(null)} style={{ background: COLORS.indigo + "20", color: COLORS.indigoLight, border: "none", padding: "8px 12px", borderRadius: 12, cursor: "pointer", fontSize: 16 }}>←</button>
                {teams.find(t => t.short_code === expandedTeam)?.name} SQUAD
             </h2>
             <button onClick={() => setExpandedTeam(null)} style={{ background: "transparent", border: "none", color: COLORS.textMuted, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>CLOSE SQUAD ×</button>
          </div>
                    {/* Add Player Form */}
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ fontSize: "11px", fontWeight: 700, color: COLORS.indigoLight, letterSpacing: "2px", textTransform: "uppercase", marginBottom: "14px" }}>
                        {editingPlayer ? "✏️ Edit Player" : `➕ Add Player to ${expandedTeam}`}
                      </div>
                      <form onSubmit={handlePlayerSubmit}>
                        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 2fr auto", gap: "10px", alignItems: "end" }}>
                          <div>
                            <label style={S.label}>Name *</label>
                            <input className="admin-input-focus" style={S.input} placeholder="Player name" value={playerForm.name} onChange={e => setPlayerForm({ ...playerForm, name: e.target.value })} required />
                          </div>
                          <div>
                            <label style={S.label}>Role</label>
                            <select className="admin-input-focus" style={S.select} value={playerForm.role} onChange={e => setPlayerForm({ ...playerForm, role: e.target.value })}>
                              <option value="BAT">🏏 Batsman</option>
                              <option value="BOWL">⚡ Bowler</option>
                              <option value="AR">🌟 All Rounder</option>
                              <option value="WK">🧤 Wicket Keeper</option>
                            </select>
                          </div>
                          <div>
                            <label style={S.label}>Credits</label>
                            <input className="admin-input-focus" type="number" step="0.5" min="1" max="15" style={S.input} value={playerForm.credit} onChange={e => setPlayerForm({ ...playerForm, credit: parseFloat(e.target.value) })} />
                          </div>
                          <div>
                            <label style={S.label}>🔗 Photo URL</label>
                            <input className="admin-input-focus" style={S.input} placeholder="https://..." value={playerForm.image} onChange={e => setPlayerForm({ ...playerForm, image: e.target.value })} />
                          </div>
                          <div style={{ display: "flex", gap: "6px", paddingTop: "20px" }}>
                            {editingPlayer && (
                              <button type="button" style={{ ...S.btnCancel, padding: "12px" }} onClick={() => { setEditingPlayer(null); setPlayerForm({ name: "", role: "BAT", credit: 9.0, image: "" }); }}>✕</button>
                            )}
                            <button type="submit" style={{ ...S.btnPrimary, padding: "12px 20px", whiteSpace: "nowrap" }}>
                              {editingPlayer ? "Update" : "+ Add"}
                            </button>
                          </div>
                        </div>
                        {/* Player image preview */}
                        {playerImgPreviewUrl && (
                          <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                            {!playerImgError && (
                              <img
                                src={playerImgPreviewUrl}
                                alt="preview"
                                onLoad={() => setPlayerImgLoaded(true)}
                                onError={() => { setPlayerImgError(true); setPlayerImgLoaded(false); }}
                                style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", border: `2px solid ${COLORS.green}`, display: playerImgLoaded ? "block" : "none" }}
                              />
                            )}
                            <span style={{ fontSize: "11px", fontWeight: 600, color: playerImgError ? COLORS.red : (playerImgLoaded ? COLORS.green : COLORS.textMuted) }}>
                              {playerImgError ? "✗ Invalid image URL" : (playerImgLoaded ? "✓ Image loaded" : "Loading...")}
                            </span>
                          </div>
                        )}
                      </form>
                    </div>

                    {/* Player Grid (Redesigned with Big Cards & Role Grouping) */}
                    {playerLoading ? (
                      <div style={{ textAlign: "center", padding: "40px", color: COLORS.textMuted }}>
                         <div style={{ width: 40, height: 40, border: "3px solid #1e293b", borderTop: "3px solid #3b82f6", borderRadius: "50%", margin: "0 auto 10px", animation: "spin 1s linear infinite" }}></div>
                         Loading squad...
                      </div>
                    ) : teamPlayers.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px", border: `1px dashed ${COLORS.cardBorder}`, borderRadius: 20, color: COLORS.textDim, fontSize: "13px" }}>
                        No players in this squad yet. Add the first player above ☝️
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 30, marginTop: 20 }}>
                        {["WK", "BAT", "AR", "BOWL"].map(roleKey => {
                          const rolePlayers = teamPlayers.filter(p => {
                            const r = (p.role || "").toUpperCase();
                            if (roleKey === "WK") return r.includes("WK") || r.includes("WICKET");
                            if (roleKey === "BAT") return r.includes("BAT");
                            if (roleKey === "AR") return r.includes("AR") || r.includes("ALL");
                            if (roleKey === "BOWL") return r.includes("BOWL");
                            return false;
                          });

                          if (rolePlayers.length === 0) return null;

                          const roleTitle = roleKey === "WK" ? "Wicket Keepers" : roleKey === "BAT" ? "Batters" : roleKey === "AR" ? "All Rounders" : "Bowlers";
                          const roleIcon = roleKey === "WK" ? "🧤" : roleKey === "BAT" ? "🏏" : roleKey === "AR" ? "🌟" : "⚡";

                          return (
                            <div key={roleKey}>
                              <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.indigoLight, marginBottom: 15, display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${COLORS.cardBorder}`, paddingBottom: 8, letterSpacing: 0.5 }}>
                                <span>{roleIcon}</span> {roleTitle.toUpperCase()}
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 16 }}>
                                {rolePlayers.map(p => (
                                  <div 
                                    key={p.id} 
                                    className="admin-card-hover"
                                    style={{ 
                                      background: COLORS.card, 
                                      border: `1px solid ${COLORS.cardBorder}`, 
                                      borderRadius: 16, 
                                      overflow: "hidden",
                                      position: "relative",
                                      transition: "all 0.3s"
                                    }}
                                  >
                                    <div style={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 4, zIndex: 10 }}>
                                      <button onClick={() => { setEditingPlayer(p.id); setPlayerForm({ name: p.name, role: p.role, credit: parseFloat(p.credit) || 9, image: p.image || "" }); }} style={{ ...S.miniBtn, width: 22, height: 22, fontSize: 10, background: "rgba(0,0,0,0.5)" }}>✏️</button>
                                      <button onClick={() => handleDeletePlayer(p.id, p.name)} style={{ ...S.miniBtn, width: 22, height: 22, fontSize: 10, background: "rgba(220,38,38,0.2)", color: COLORS.red }}>🗑️</button>
                                    </div>

                                    {/* Player Image */}
                                    <div style={{ height: 140, background: `linear-gradient(180deg, ${COLORS.indigo}10 0%, ${COLORS.indigo}20 100%)`, display: "flex", alignItems: "flex-end", justifyContent: "center", overflow: "hidden" }}>
                                      {p.image && isValidUrl(p.image) ? (
                                        <img src={p.image} alt="" style={{ width: "85%", height: "85%", objectFit: "contain", filter: "drop-shadow(0 5px 10px rgba(0,0,0,0.3))" }} />
                                      ) : (
                                        <div style={{ fontSize: 40, opacity: 0.2, marginBottom: 15 }}>👤</div>
                                      )}
                                    </div>

                                    {/* Info */}
                                    <div style={{ padding: 10, textAlign: "center", borderTop: `1px solid ${COLORS.cardBorder}` }}>
                                      <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.text, textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                                      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 4 }}>
                                        <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted }}>{p.role}</span>
                                        <span style={{ color: COLORS.textDim, fontSize: 7 }}>•</span>
                                        <span style={{ fontSize: 9, fontWeight: 800, color: COLORS.amber }}>{p.credit} CR</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
          </div>
      )}
    </div>
  );
};

const ManageTeams = () => (
  <ToastProvider>
    <ManageTeamsInner />
  </ToastProvider>
);

export default ManageTeams;
